import ApiErrorBanner from '@/components/ApiErrorBanner';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import { FetchProvider, useFetchEntity } from '@/support/Hooks/fetch';
import { useNavigate, useParams } from 'react-router-dom';
import AnimalsTable from './AnimalsTable/AnimalsTable';
import Averages from './Averages';
import PlannedAnimalsTable from '@/components/Animals/PlannedAnimals/PlannedAnimalsTable.tsx';
import { useSelector } from 'react-redux';
import { Study } from '@/model/Study.model';
import { _isNil, _notNil } from '@/littledash';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { useEffect, useState } from 'react';
import { useQueryParams } from '@/support/Hooks/useQueryParams/useQueryParams';
import { Nullable } from '@/model/Common.model';
import { MetadataField } from '@/model/Metadata.model.ts';
import { mapMetadataResponse, MetadataGlossaryItem } from '@/components/Studies/Create/Steps/Animals/Animals.utils.tsx';
import { SpeciesV1 } from '@/model/Species.model.ts';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors.tsx';

enum ActiveTabKey {
  registered = 0,
  planned = 1,
  averages = 2,
}

const getTabIndexFromKey = (tabKey: keyof typeof ActiveTabKey, showPlannedTab: boolean): ActiveTabKey => {
  if (tabKey === 'registered') {
    return ActiveTabKey.registered;
  }
  if (tabKey === 'planned') {
    return showPlannedTab ? ActiveTabKey.planned : ActiveTabKey.registered;
  }
  // For 'averages', return the correct index based on whether planned tab is shown
  return showPlannedTab ? ActiveTabKey.averages : 1;
};

export interface AnimalsContextProps {
  entity: Study;
  metadataOptions: Array<MetadataField>;
  speciesData: Array<SpeciesV1>;
}

const featureSelector = createSelector([featuresSelector], (features) => ({
  plannedAnimalsEnabled: features?.planned_animals ?? false,
}));

const Animals = () => {
  const { id: studyId } = useParams();
  const queryParams = useQueryParams();
  const navigate = useNavigate();

  const entityProps = useFetchEntity<Study>({
    entityType: 'study',
    params: { id: studyId },
    includes: 'users,metadata,study_groups',
  });
  const { entity: study, entityLoading: studyLoading, entityError: studyFetchError } = entityProps;
  const { plannedAnimalsEnabled } = useSelector(featureSelector);
  const [hasPlannedAnimals, setHasPlannedAnimals] = useState<boolean>(false);
  const [metadataOptions, setMetadataOptions] = useState<Array<MetadataField>>([]);
  const [speciesData, setSpeciesData] = useState<Array<SpeciesV1>>([]);
  const showPlannedTab = plannedAnimalsEnabled && hasPlannedAnimals;

  const [activeTab, setActiveTab] = useState<ActiveTabKey>(() => {
    const tabKey = queryParams?.get('tab') as Nullable<keyof typeof ActiveTabKey>;
    return getTabIndexFromKey(tabKey ?? 'registered', showPlannedTab);
  });

  const handleTabChange = (tabIndex: ActiveTabKey) => {
    setActiveTab(tabIndex);
    const updatedQueryParams = new URLSearchParams(queryParams.toString());
    if (tabIndex === ActiveTabKey.registered) {
      updatedQueryParams.delete('tab');
    } else {
      // Map the tab index to the correct key name, accounting for conditional planned tab
      let tabKey: keyof typeof ActiveTabKey;
      if (showPlannedTab) {
        // All tabs are shown, use direct mapping
        tabKey = ActiveTabKey[tabIndex] as keyof typeof ActiveTabKey;
      } else {
        // Planned tab is hidden, so tab index 1 should map to 'averages'
        tabKey = tabIndex === 1 ? 'averages' : (ActiveTabKey[tabIndex] as keyof typeof ActiveTabKey);
      }
      updatedQueryParams.set('tab', tabKey);
    }
    navigate({ search: updatedQueryParams.toString() });
  };
  useEffect(() => {
    const tabKey = queryParams?.get('tab') as Nullable<keyof typeof ActiveTabKey>;
    setActiveTab(getTabIndexFromKey(tabKey ?? 'registered', showPlannedTab));
  }, [queryParams, showPlannedTab]);

  const {
    response: plannedAnimalsResponse,
    invoke: fetchPlannedAnimals,
    loading: plannedAnimalsLoading,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/animals',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false } },
  });

  const { response: metadataResponse, loading: metadataLoading } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: false,
      filter_type: 'subject_meta',
    },
    invokeOnInit: true,
    options: { onError: { throw: false, capture: true, toast: false, slug: 'fetch-study-metadata' } },
  });

  useEffect(() => {
    if (_notNil(metadataResponse?.body)) {
      const metadata = mapMetadataResponse((metadataResponse?.body || []) as Array<MetadataGlossaryItem>);
      setMetadataOptions(metadata);
    }
  }, [metadataResponse]);

  const { response: speciesResponse, loading: speciesLoading } = useApiHook({
    endpoint: 'GET /api/v1/species',
    invokeOnInit: true,
    query: { include: ['strains'] },
    options: { onError: { throw: false, capture: true, toast: false, slug: 'fetch-study-species' } },
  });

  useEffect(() => {
    if (speciesResponse?.type === 'success') {
      const species = speciesResponse?.body?.data ?? [];
      setSpeciesData(species);
    }
  }, [speciesResponse]);

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (studyFetchError) {
    return (
      <ApiErrorBanner
        title="There was an error fetching study data"
        text="An error has occurred when fetching study data, please try again. If this keeps occurring please contact support."
        error={studyFetchError}
      />
    );
  }
  if (studyLoading || _isNil(study)) {
    return null;
  }

  const invokePlannedAnimals: () => Promise<void> = async () => {
    if (plannedAnimalsEnabled) {
      const response = await fetchPlannedAnimals({
        path: { studyApiId: study.api_id },
        query: { page: 1, perPage: 5 },
      });
      if (_notNil(response?.body?.data) && response?.body?.data !== undefined) {
        setHasPlannedAnimals((response?.body?.data ?? []).length > 0);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (!plannedAnimalsResponse && !plannedAnimalsLoading) {
    invokePlannedAnimals();
  }

  if (plannedAnimalsLoading || metadataLoading || speciesLoading) {
    return null;
  }

  const contextValue: AnimalsContextProps = {
    ...entityProps,
    entity: study,
    metadataOptions,
    speciesData,
  };

  return (
    <FetchProvider {...contextValue}>
      <div className="ph4 pt3">
        <Tabs outerState={[activeTab, handleTabChange]}>
          <div className="mb4 bb b--moon-gray">
            <Tab className="f4" testId="animals-tab">
              Animals
            </Tab>
            {showPlannedTab && (
              <Tab className="f4" testId="plannedanimals-tab">
                Planned Animals
              </Tab>
            )}
            <Tab className="f4">Averages</Tab>
          </div>
          <div>
            <Panel>
              <AnimalsTable hasPlannedAnimals={hasPlannedAnimals} />
            </Panel>
            {showPlannedTab && (
              <Panel>
                <PlannedAnimalsTable />
              </Panel>
            )}
            <Panel>
              <Averages />
            </Panel>
          </div>
        </Tabs>
      </div>
    </FetchProvider>
  );
};

export default Animals;
